import type { Asset, DividendRecord, SupportedCurrency } from '../types';
import type { MarketQuote } from '../services/marketDataService';
import { convertCurrency } from './currency';
import { resolveLiveQuoteForAsset } from './quoteLookup';
import { canonicalMoneyCurrency, getDisplayQuotePrice, isBackendNormalizedQuote, normalizeMoneyUnit } from './quoteNormalization';

export type CurrencyDisplayMode = 'holding' | 'preferred';

export type HoldingDisplayRow = {
    asset: Asset;
    symbol: string;
    name: string;
    quantity: number;
    nativeCurrency: string;
    baseCurrency: string;
    displayCurrency: string;
    costBasisNative: number;
    costBasisBase: number;
    avgCostNative: number;
    avgCostBase: number;
    livePriceNative: number | null;
    livePriceBase: number | null;
    currentValueNative: number | null;
    currentValueBase: number | null;
    historicDividendsBase: number;
    historicDividendsNative: number;
    dividendReceivedPerShareNative: number;
    dividendReceivedPerShareBase: number;
    receivedYieldOnCostPctNative: number;
    receivedYieldOnCostPctBase: number;
    receivedCurrentYieldPctNative: number;
    receivedCurrentYieldPctBase: number;
    forwardAnnualDividendNative: number;
    forwardAnnualDividendBase: number;
    forwardDividendPerShareNative: number;
    dividendYieldPct: number;
    profitNative: number | null;
    profitBase: number | null;
    profitPctNative: number | null;
    profitPctBase: number | null;
    dailyNative: number | null;
    dailyBase: number | null;
    dailyPct: number | null;
    warnings: string[];
};

export type BuildHoldingDisplayRowsOptions = {
    assets: Asset[];
    selectedCurrency: SupportedCurrency | string;
    displayCurrencyMode?: CurrencyDisplayMode;
    liveQuotes?: Record<string, MarketQuote>;
};

const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const normalCurrency = (value?: string | null, fallback = 'GBP') => (value || fallback).trim().toUpperCase();

const normalizeCandidateCurrency = (value?: string | null): string | null => {
    const raw = typeof value === 'string' ? value.trim().toUpperCase() : '';
    if (!raw) return null;
    const canonical = canonicalMoneyCurrency(raw, '');
    return canonical || null;
};

const hasPositive = (value: unknown) => Number.isFinite(Number(value)) && Number(value) > 0;

const isDevelopment = () => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') return true;
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
};

const isDebugQuotesEnabled = () => {
    try {
        return localStorage.getItem('debugQuotes') === '1';
    } catch {
        return false;
    }
};


const warnQuoteUnitCheck = (details: Record<string, unknown>) => {
    if (!isDevelopment()) return;
    console.warn('[holdings] Quote unit check', details);
};

export const getAssetNativeCurrency = (asset: Asset, quote?: MarketQuote, fallbackCurrency = 'GBP'): string => {
    const anyAsset = asset as any;
    const costCurrency = normalizeCandidateCurrency(anyAsset.costCurrency || anyAsset.totalCurrency);
    const baseOrCostCurrency = costCurrency || normalizeCandidateCurrency(asset.baseCurrency) || normalizeCandidateCurrency(asset.currency);
    const nativeCurrency = normalizeCandidateCurrency(asset.nativeCurrency);
    const trustedNativeCurrency = nativeCurrency && (!baseOrCostCurrency || nativeCurrency !== baseOrCostCurrency) ? nativeCurrency : null;

    const candidates = [
        quote ? quote.currency : null,
        asset.quoteCurrency,
        anyAsset.originalPriceCurrency,
        asset.priceCurrency,
        anyAsset.instrumentMapCurrency,
        anyAsset.instrumentCurrency,
        anyAsset.marketCurrency,
        trustedNativeCurrency,
        fallbackCurrency,
        asset.baseCurrency,
        asset.currency,
        nativeCurrency,
        'GBP',
    ];

    for (const candidate of candidates) {
        const normalized = normalizeCandidateCurrency(candidate);
        if (normalized) return normalized;
    }

    return 'GBP';
};

const getProviderForwardAnnualDividendNative = (asset: Asset, quantity: number, nativeCurrency: string): number => {
    const anyAsset = asset as any;
    const annualTotal = toNumber(anyAsset.forwardAnnualDividend ?? anyAsset.annualDividend ?? anyAsset.dividendsForwardAnnual ?? anyAsset.annualDividendEstimate);
    if (annualTotal > 0) return annualTotal;

    const perShare = toNumber(anyAsset.forwardDividendPerShare ?? anyAsset.annualDividendPerShare ?? anyAsset.dividendPerShare ?? anyAsset.dividendRate);
    if (perShare > 0 && quantity > 0) return perShare * quantity;

    return 0;
};

const getEventPerShareNative = (record: DividendRecord, asset: Asset, nativeCurrency: string): number => {
    const anyRecord = record as any;
    const perShareRaw = toNumber(anyRecord.amountPerShare ?? anyRecord.dividendPerShare ?? anyRecord.pricePerShare);
    if (perShareRaw > 0) {
        const normalized = normalizeMoneyUnit({
            value: perShareRaw,
            rawCurrency: anyRecord.priceCurrency || anyRecord.currency || nativeCurrency,
            exchange: asset.exchange,
            symbol: asset.symbol,
        });
        return normalized.currency === nativeCurrency ? normalized.value : convertCurrency(normalized.value, normalized.currency, nativeCurrency);
    }

    const qty = toNumber(record.quantityAtDate);
    if (qty > 0 && toNumber(record.amount) > 0) {
        const recordCurrency = normalCurrency(anyRecord.currency, asset.baseCurrency || nativeCurrency);
        const nativeAmount = recordCurrency === nativeCurrency ? toNumber(record.amount) : convertCurrency(toNumber(record.amount), recordCurrency, nativeCurrency);
        return nativeAmount / qty;
    }

    return 0;
};

const inferForwardAnnualDividendNativeFromEvents = (asset: Asset, quantity: number, nativeCurrency: string, warnings: string[]): number => {
    const records = Array.isArray(asset.dividends) ? asset.dividends : [];
    if (!records.length || quantity <= 0) return 0;

    const consistentRecords = records.filter((record) => {
        const qtyAtDate = toNumber(record.quantityAtDate);
        if (qtyAtDate > quantity * 2) return false;
        return getEventPerShareNative(record, asset, nativeCurrency) > 0;
    });

    if (!consistentRecords.length) return 0;

    const sorted = [...consistentRecords].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const latestPerShare = getEventPerShareNative(sorted[0], asset, nativeCurrency);
    const annualEvents = consistentRecords.length >= 3 ? Math.min(12, consistentRecords.length) : 4;
    if (latestPerShare > 0) {
        warnings.push('Forward dividend is estimated from recent imported dividend-per-share events.');
        return latestPerShare * annualEvents * quantity;
    }
    return 0;
};

export const buildHoldingDisplayRows = ({
    assets,
    selectedCurrency,
    displayCurrencyMode = 'preferred',
    liveQuotes = {},
}: BuildHoldingDisplayRowsOptions): HoldingDisplayRow[] => {
    const baseCurrency = normalCurrency(selectedCurrency);

    return assets.map((asset) => {
        const quote = resolveLiveQuoteForAsset(asset, liveQuotes);
        const quantity = toNumber(asset.quantity);
        const nativeCurrency = getAssetNativeCurrency(asset, quote, baseCurrency);
        const displayCurrency = displayCurrencyMode === 'holding' ? nativeCurrency : baseCurrency;
        const warnings: string[] = [];

        const costBasisBase = toNumber(asset.totalInvested ?? quantity * toNumber(asset.avgPrice));
        const costBasisNative = toNumber(asset.totalInvestedNative ?? quantity * toNumber(asset.avgPriceNative || asset.avgPrice));
        const avgCostBase = quantity > 0 ? costBasisBase / quantity : toNumber(asset.avgPrice);
        const avgCostNative = quantity > 0 ? costBasisNative / quantity : toNumber(asset.avgPriceNative || asset.avgPrice);

        const quoteCurrency = normalizeMoneyUnit({
            value: 1,
            rawCurrency: quote?.currency || asset.quoteCurrency || nativeCurrency,
            exchange: asset.exchange,
            symbol: asset.symbol,
        }).currency;
        const liveQuotePrice = getDisplayQuotePrice(quote);
        const livePriceNative = hasPositive(liveQuotePrice)
            ? quoteCurrency === nativeCurrency
                ? liveQuotePrice
                : convertCurrency(liveQuotePrice!, quoteCurrency, nativeCurrency)
            : null;
        const canonicalQuoteKey = (quote as any)?.canonicalQuoteKey || (quote as any)?.providerKey || (asset as any).canonicalQuoteKey;
        const quoteFound = Boolean(quote && hasPositive(liveQuotePrice));
        const usingFallback = false;
        console.info('[portfolio-display] quote-state', {
            symbol: asset.symbol,
            instrumentId: asset.instrumentId,
            canonicalQuoteKey,
            quoteFound,
            quotePrice: quoteFound ? liveQuotePrice : null,
            avgCost: avgCostNative,
            usingFallback,
        });
        const livePriceBase = livePriceNative != null ? convertCurrency(livePriceNative, nativeCurrency, baseCurrency) : null;
        const currentValueNative = livePriceNative != null ? quantity * livePriceNative : null;
        const currentValueBase = currentValueNative != null ? convertCurrency(currentValueNative, nativeCurrency, baseCurrency) : null;

        const quoteRatio = livePriceNative != null && avgCostNative > 0 ? livePriceNative / avgCostNative : null;
        const quoteUnitWarning = quoteRatio != null && (quoteRatio > 20 || quoteRatio < 0.05);
        if (quoteUnitWarning) {
            warnings.push('Quote unit check');
            warnQuoteUnitCheck({
                symbol: asset.symbol,
                price: livePriceNative,
                avgCostNative,
                quoteUnit: (quote as any)?.quoteUnit,
                unitScale: quote?.unitScale,
                quoteRatio,
                reason: quoteRatio > 20 ? 'live price is more than 20x average cost' : 'live price is less than 5% of average cost',
            });
        }

        const historicDividendsBaseRaw = toNumber((asset as any).historicDividendsReceived ?? (asset as any).importedDividendCash ?? asset.totalDividends);
        const historicDividendsNativeRaw = toNumber((asset as any).historicDividendsReceivedNative ?? (asset as any).importedDividendCashNative);
        const historicDividendsNative = historicDividendsNativeRaw || convertCurrency(historicDividendsBaseRaw, baseCurrency, nativeCurrency);
        const historicDividendsBase = historicDividendsBaseRaw || convertCurrency(historicDividendsNativeRaw, nativeCurrency, baseCurrency);
        const dividendReceivedPerShareNative = quantity > 0 ? historicDividendsNative / quantity : 0;
        const dividendReceivedPerShareBase = quantity > 0 ? historicDividendsBase / quantity : 0;
        const receivedYieldOnCostPctNative = costBasisNative > 0 ? (historicDividendsNative / costBasisNative) * 100 : 0;
        const receivedYieldOnCostPctBase = costBasisBase > 0 ? (historicDividendsBase / costBasisBase) * 100 : 0;
        const receivedCurrentYieldPctNative = currentValueNative && currentValueNative > 0 ? (historicDividendsNative / currentValueNative) * 100 : 0;
        const receivedCurrentYieldPctBase = currentValueBase && currentValueBase > 0 ? (historicDividendsBase / currentValueBase) * 100 : 0;

        const maxDividendQuantity = Array.isArray(asset.dividends)
            ? asset.dividends.reduce((max, record) => Math.max(max, toNumber(record.quantityAtDate)), 0)
            : 0;
        if (quantity > 0 && maxDividendQuantity > quantity * 2) {
            warnings.push('Dividend history may be incomplete or out of sync with imported buy history. Upload full broker history for accurate historical return calculations.');
        }

        let forwardAnnualDividendNative = getProviderForwardAnnualDividendNative(asset, quantity, nativeCurrency);
        const providerYield = toNumber((asset as any).dividendYield ?? (asset as any).forwardDividendYield ?? (asset as any).yield);
        if (forwardAnnualDividendNative <= 0 && providerYield > 0 && currentValueNative != null) {
            const yieldFraction = providerYield > 1 ? providerYield / 100 : providerYield;
            forwardAnnualDividendNative = currentValueNative * yieldFraction;
        }
        if (forwardAnnualDividendNative <= 0 && !warnings.some((warning) => warning.includes('out of sync'))) {
            forwardAnnualDividendNative = inferForwardAnnualDividendNativeFromEvents(asset, quantity, nativeCurrency, warnings);
        }
        const forwardAnnualDividendBase = convertCurrency(forwardAnnualDividendNative, nativeCurrency, baseCurrency);
        const forwardDividendPerShareNative = quantity > 0 ? forwardAnnualDividendNative / quantity : 0;
        const dividendYieldPct = providerYield > 0
            ? (providerYield > 1 ? providerYield : providerYield * 100)
            : currentValueNative && currentValueNative > 0 && forwardAnnualDividendNative > 0
              ? (forwardAnnualDividendNative / currentValueNative) * 100
              : 0;

        const profitNative = currentValueNative != null ? currentValueNative - costBasisNative : null;
        const profitBase = currentValueBase != null ? currentValueBase - costBasisBase : null;
        const profitPctNative = !quoteUnitWarning && profitNative != null && costBasisNative > 0 ? (profitNative / costBasisNative) * 100 : null;
        const profitPctBase = !quoteUnitWarning && profitBase != null && costBasisBase > 0 ? (profitBase / costBasisBase) * 100 : null;

        const dailyQuote = Number.isFinite(Number(quote?.change)) ? toNumber(quote?.change) * quantity : null;
        const dailyNative = dailyQuote != null ? (quoteCurrency === nativeCurrency ? dailyQuote : convertCurrency(dailyQuote, quoteCurrency, nativeCurrency)) : null;
        const dailyBase = dailyNative != null ? convertCurrency(dailyNative, nativeCurrency, baseCurrency) : null;
        const dailyPct = Number.isFinite(Number(quote?.changePercent)) ? Number(quote?.changePercent) : null;

        const row: HoldingDisplayRow = {
            asset,
            symbol: asset.symbol,
            name: asset.name,
            quantity,
            nativeCurrency,
            baseCurrency,
            displayCurrency,
            costBasisNative,
            costBasisBase,
            avgCostNative,
            avgCostBase,
            livePriceNative,
            livePriceBase,
            currentValueNative,
            currentValueBase,
            historicDividendsBase,
            historicDividendsNative,
            dividendReceivedPerShareNative,
            dividendReceivedPerShareBase,
            receivedYieldOnCostPctNative,
            receivedYieldOnCostPctBase,
            receivedCurrentYieldPctNative,
            receivedCurrentYieldPctBase,
            forwardAnnualDividendNative,
            forwardAnnualDividendBase,
            forwardDividendPerShareNative,
            dividendYieldPct,
            profitNative,
            profitBase,
            profitPctNative,
            profitPctBase,
            dailyNative,
            dailyBase,
            dailyPct,
            warnings,
        };

        if (isDebugQuotesEnabled()) {
            console.debug('[holding-display] currency-decision', {
                symbol: asset.symbol,
                name: asset.name,
                quantity,

                assetCurrency: asset.currency,
                assetNativeCurrency: asset.nativeCurrency,
                assetPriceCurrency: asset.priceCurrency,
                assetQuoteCurrency: asset.quoteCurrency,
                assetBaseCurrency: asset.baseCurrency,
                assetExchange: asset.exchange,
                assetInstrumentId: asset.instrumentId,
                assetDataProviderId: asset.dataProviderId,

                selectedCurrency: baseCurrency,
                displayCurrencyMode,
                resolvedNativeCurrency: nativeCurrency,
                displayCurrency,

                avgCostNative,
                avgCostBase,
                costBasisNative,
                costBasisBase,

                quoteProviderId: quote?.providerId,
                quotePrice: quote?.price,
                quoteRawPrice: quote?.rawPrice,
                quoteCurrency: quote?.currency,
                quoteRawCurrency: quote?.rawCurrency,
                quoteUnit: (quote as any)?.quoteUnit,
                quoteUnitScale: quote?.unitScale,
                quoteSource: (quote as any)?.source,
                quoteFetchedAt: quote?.fetchedAt,
                isBackendNormalizedQuote: isBackendNormalizedQuote(quote),

                resolvedQuoteCurrency: quoteCurrency,
                livePriceNative,
                livePriceBase,
                currentValueNative,
                currentValueBase,
                profitNative,
                profitBase,
                profitPctNative,
                profitPctBase,
                quoteRatio,
                quoteUnitWarning,
                quoteUnitWarningReason: quoteUnitWarning
                    ? quoteRatio != null && quoteRatio > 20
                        ? 'live price is more than 20x average cost'
                        : 'live price is less than 5% of average cost'
                    : null,
                warnings,
            });
        }

        return row;
    });
};

export const selectDisplayValue = (row: HoldingDisplayRow, nativeValue: number | null, baseValue: number | null) =>
    row.displayCurrency === row.nativeCurrency ? nativeValue : baseValue;
